import React, { useMemo, useState, useEffect, useRef } from "react";

import { Input, Spin, Table, notification, Dropdown, Checkbox } from "antd";

import {
  CloseOutlined,
  SearchOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  MailOutlined,
} from "@ant-design/icons";

import AddPanelUserModal from "../../../components/Modals/AddPanelUserModal";
import ConfirmationModal from "../../../components/Modals/ConfirmationModal";
import SendPanelLinkModal from "../../../components/Modals/SendPanelLinkModal";
import PanelUserEmailConflictModal from "../../../components/Modals/PanelUserEmailConflictModal";
import { ACTIONS_DROPDOWN_PROPS } from "../../../constants/actionsDropdown";
import {
  useGetPanelUsersQuery,
  useGetFormsQuery,
  useSendPanelUserSurveyLinkMutation,
  useDeletePanelUserMutation,
} from "../../../store/api/apiSlice";

import {
  buildEditPanelUserData,
  buildSurveyLink,
  checkPanelUserEmailsForSurveyLink,
  getActivePanelForms,
  mapPanelUsersToTableData,
} from "./helpers";
import { getFormOrganizationDisplayName } from "../../../utils/formTabs";
import { buildConditionalPagination } from "../../../utils/pagination";

const PanelUser = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [surveySearchInput, setSurveySearchInput] = useState("");
  const [surveyDropdownOpen, setSurveyDropdownOpen] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingPanelUser, setEditingPanelUser] = useState(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [panelUserToDelete, setPanelUserToDelete] = useState(null);
  const [sendLinkModalVisible, setSendLinkModalVisible] = useState(false);
  const [emailConflictModalVisible, setEmailConflictModalVisible] =
    useState(false);
  const [emailConflicts, setEmailConflicts] = useState([]);
  const [eligibleConflictUsers, setEligibleConflictUsers] = useState([]);
  const [canRemoveConflictEmails, setCanRemoveConflictEmails] = useState(false);
  const [pendingSendPayload, setPendingSendPayload] = useState(null);

  const surveySearchRef = useRef(null);

  const queryParams = useMemo(() => ({}), []);

  const {
    data: panelUsers,
    isLoading,
    refetch,
  } = useGetPanelUsersQuery(queryParams);

  const { data: formsData, isFetching: isSurveySearchLoading } =
    useGetFormsQuery(
      {
        type: "panel",
        status: "active",
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const [sendPanelUserSurveyLink, { isLoading: isSendingSurveyLink }] =
    useSendPanelUserSurveyLinkMutation();
  const [deletePanelUser] = useDeletePanelUserMutation();

  useEffect(() => {
    refetch();
  }, [queryParams, refetch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        surveySearchRef.current &&
        !surveySearchRef.current.contains(event.target)
      ) {
        setSurveyDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSurveySearchChange = (e) => {
    const value = e.target.value;

    setSurveySearchInput(value);

    setSelectedSurvey(null);

    setSurveyDropdownOpen(true);
  };

  const clearSurveySearch = () => {
    setSurveySearchInput("");

    setSelectedSurvey(null);

    setSurveyDropdownOpen(false);
  };

  const resetSendLinkSelection = () => {
    setSelectedRowKeys([]);
    clearSurveySearch();
  };

  const handleSelectSurvey = (form) => {
    setSelectedSurvey({
      id: form._id,
      title: form.title,
      link: buildSurveyLink(form._id),
    });

    setSurveySearchInput(form.title || "");

    setSurveyDropdownOpen(false);
  };

  const surveyOptions = useMemo(
    () => getActivePanelForms(formsData, surveySearchInput),
    [formsData, surveySearchInput],
  );

  const handleEdit = (record) => {
    setEditingPanelUser(buildEditPanelUserData(record));
    setEditModalVisible(true);
  };

  const showDeleteConfirm = (record) => {
    setPanelUserToDelete(record);
    setDeleteConfirmVisible(true);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmVisible(false);
    setPanelUserToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!panelUserToDelete) {
      return;
    }

    try {
      await deletePanelUser(panelUserToDelete._id).unwrap();
      setSelectedRowKeys((prev) =>
        prev.filter((key) => key !== panelUserToDelete._id),
      );
      refetch();
      handleDeleteCancel();
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const getActionsMenuItems = () => [
    { key: "edit", label: "Edit", icon: <EditOutlined /> },
    {
      key: "delete",
      label: "Delete",
      danger: true,
      icon: <DeleteOutlined />,
    },
  ];

  const handleActionMenuClick = (record, actionKey, event) => {
    event?.domEvent?.stopPropagation?.();

    switch (actionKey) {
      case "edit":
        handleEdit(record);
        break;
      case "delete":
        showDeleteConfirm(record);
        break;
      default:
        break;
    }
  };

  const columns = [
    {
      title: "Sr#",
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Full name",
      key: "name",
      render: (_, record) => (
        <span className="font-medium text-gray-900">
          {record.panelUser.name || "N/A"}
        </span>
      ),
    },
    {
      title: "Email",
      key: "email",
      render: (_, record) => (
        <span className="text-gray-900">{record.panelUser.email || "N/A"}</span>
      ),
    },
    {
      title: "Joined Date",
      dataIndex: "joinedDate",
      key: "joinedDate",
      width: 130,
      render: (joinedDate) => joinedDate || "N/A",
    },
    {
      title: "",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <div
          className="flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            {...ACTIONS_DROPDOWN_PROPS}
            overlayClassName="actions-dropdown-overlay panel-user-actions-dropdown"
            menu={{
              items: getActionsMenuItems(record),
              onClick: (event) =>
                handleActionMenuClick(record, event.key, event),
            }}
          >
            <button
              type="button"
              className="actions-dots-btn"
              onClick={(e) => e.stopPropagation()}
              aria-label={`Open actions for ${
                record?.panelUser?.name ||
                record?.panelUser?.email ||
                "panel user"
              }`}
            >
              <MoreOutlined />
            </button>
          </Dropdown>
        </div>
      ),
    },
  ];

  const tableData = useMemo(
    () => mapPanelUsersToTableData(panelUsers),
    [panelUsers],
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      preserveSelectedRowKeys: true,
      columnTitle: (
        <Checkbox
          checked={
            tableData.length > 0 &&
            selectedRowKeys.length === tableData.length
          }
          onChange={(e) => {
            setSelectedRowKeys(
              e.target.checked ? tableData.map((row) => row._id) : [],
            );
          }}
        />
      ),
      onChange: (keys) => {
        setSelectedRowKeys(keys);
      },
    }),
    [selectedRowKeys, tableData],
  );

  const selectedRows = useMemo(
    () => tableData.filter((row) => selectedRowKeys.includes(row._id)),
    [tableData, selectedRowKeys],
  );

  const selectedEmails = useMemo(
    () =>
      selectedRows
        .map((row) => row.panelUser?.email)
        .filter(Boolean),
    [selectedRows],
  );

  const tablePagination = buildConditionalPagination(tableData);

  const showEmailConflictReview = (
    conflicts,
    eligibleUsers,
    eligibleEmails,
    pendingPayload = null,
  ) => {
    setEmailConflicts(conflicts);
    setEligibleConflictUsers(eligibleUsers);
    setCanRemoveConflictEmails(eligibleEmails.length > 0);
    setPendingSendPayload(pendingPayload);
    setSendLinkModalVisible(false);
    setEmailConflictModalVisible(true);
  };

  const handleOpenSendLinkModal = () => {
    if (!selectedSurvey?.link) {
      notification.error({
        message: "Form required",
        description: "Search and select a panel form before sending the link.",
      });

      return;
    }

    if (!selectedEmails.length) {
      notification.error({
        message: "Panel users required",
        description: "Select at least one panel user using the checkboxes.",
      });

      return;
    }

    const { conflicts, eligibleEmails, eligibleUsers } =
      checkPanelUserEmailsForSurveyLink(
        panelUsers,
        selectedEmails,
        selectedSurvey.link,
      );

    if (conflicts.length) {
      showEmailConflictReview(conflicts, eligibleUsers, eligibleEmails);
      return;
    }

    setSendLinkModalVisible(true);
  };

  const sendSurveyLinkToEmails = async (
    emails,
    { message_body, header_subtitle },
    surveyLink = selectedSurvey?.link,
  ) => {
    if (!surveyLink) {
      notification.error({
        message: "Form required",
        description: "Search and select a panel form before sending the link.",
      });
      return;
    }

    const { conflicts, eligibleEmails, eligibleUsers } =
      checkPanelUserEmailsForSurveyLink(panelUsers, emails, surveyLink);

    if (conflicts.length) {
      showEmailConflictReview(
        conflicts,
        eligibleUsers,
        eligibleEmails,
        { message_body, header_subtitle },
      );
      return;
    }

    const emailsToSend = eligibleEmails.length ? eligibleEmails : emails;

    if (!emailsToSend.length) {
      notification.warning({
        message: "No eligible panel users",
        description:
          "All selected panel users already have this form assigned or submitted.",
      });
      return;
    }

    await sendPanelUserSurveyLink({
      emails: emailsToSend,
      survey_link: surveyLink,
      message: message_body,
      header_subtitle,
    }).unwrap();

    notification.success({
      message: "Success",
      description: `Survey link sent to ${emailsToSend.length} panel user${
        emailsToSend.length === 1 ? "" : "s"
      }.`,
    });

    setSendLinkModalVisible(false);
    setEmailConflictModalVisible(false);
    setEmailConflicts([]);
    setEligibleConflictUsers([]);
    setCanRemoveConflictEmails(false);
    setPendingSendPayload(null);
    resetSendLinkSelection();
  };

  const handleSendSurveyLink = async ({ message_body, header_subtitle }) => {
    try {
      await sendSurveyLinkToEmails(selectedEmails, {
        message_body,
        header_subtitle,
      });
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          error.data?.message || "Failed to send survey link to panel users",
      });
    }
  };

  const handleRemoveUsedEmailsAndSend = () => {
    const conflictEmails = new Set(
      emailConflicts
        .map((item) => String(item.email || "").trim().toLowerCase())
        .filter(Boolean),
    );
    const remainingEmails = selectedEmails.filter(
      (email) => !conflictEmails.has(String(email).trim().toLowerCase()),
    );
    const remainingRowIds = selectedRows
      .filter(
        (row) =>
          !conflictEmails.has(
            String(row.panelUser?.email || "").trim().toLowerCase(),
          ),
      )
      .map((row) => row._id);

    setSelectedRowKeys(remainingRowIds);
    setEmailConflictModalVisible(false);
    setEmailConflicts([]);
    setEligibleConflictUsers([]);
    setCanRemoveConflictEmails(false);

    if (!remainingEmails.length) {
      notification.warning({
        message: "No eligible panel users",
        description:
          "All selected panel users already have this form assigned or submitted.",
      });
      setPendingSendPayload(null);
      return;
    }

    if (pendingSendPayload) {
      sendSurveyLinkToEmails(remainingEmails, pendingSendPayload).catch(
        (error) => {
          notification.error({
            message: "Error",
            description:
              error.data?.message ||
              "Failed to send survey link to panel users",
          });
        },
      );
      return;
    }

    setPendingSendPayload(null);
    setSendLinkModalVisible(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6 min-h-screen">
      <div className="flex justify-between items-center selectSecondary">
        <h3 className="text-md font-semibold m-0">Overview</h3>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div ref={surveySearchRef} className="relative flex-1 min-w-[280px]">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Survey link
          </label>
          <Input
            value={surveySearchInput}
            placeholder="Search active panel forms..."
            prefix={<SearchOutlined className="text-lg" />}
            suffix={
              surveySearchInput ? (
                <CloseOutlined
                  className="cursor-pointer hover:text-gray-600"
                  onClick={clearSurveySearch}
                />
              ) : null
            }
            onChange={handleSurveySearchChange}
            onFocus={() => setSurveyDropdownOpen(true)}
            className="h-11 !rounded-2xl bg-white"
          />
          {surveyDropdownOpen && (
            <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-60 overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-lg">
              {isSurveySearchLoading ? (
                <div className="flex justify-center py-6">
                  <Spin size="small" />
                </div>
              ) : surveyOptions.length ? (
                surveyOptions.map((form) => (
                  <button
                    key={form._id}
                    type="button"
                    onClick={() => handleSelectSurvey(form)}
                    className="flex w-full flex-col items-start border-0 border-b border-gray-100 bg-white px-4 py-3 text-left last:border-b-0 hover:bg-gray-50 cursor-pointer"
                  >
                    <span className="w-full truncate font-medium text-gray-900">
                      {form.title || "Untitled form"}
                    </span>
                    <span className="w-full truncate text-xs text-gray-500">
                      {getFormOrganizationDisplayName(form) || "N/A"}
                    </span>
                  </button>
                ))
              ) : (
                <div className="px-4 py-6 text-center text-sm text-gray-500">
                  No active panel forms found
                </div>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleOpenSendLinkModal}
          disabled={isSendingSurveyLink}
          aria-label="Send panel form link"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border-0 bg-[#18181B] text-white hover:opacity-90 disabled:opacity-60"
        >
          <MailOutlined className="text-lg" />
        </button>
      </div>
      <div className="bg-white rounded-xl overflow-hidden">
        <Table
          rowKey="_id"
          loading={isLoading}
          rowSelection={rowSelection}
          columns={columns}
          dataSource={tableData}
          pagination={tablePagination}
          className="forms-table panel-user-table"
          rowClassName="hover:bg-gray-50"
          scroll={{ x: "100%" }}
        />
      </div>
      <AddPanelUserModal
        visible={editModalVisible}
        onClose={() => {
          setEditModalVisible(false);
          setEditingPanelUser(null);
        }}
        editData={editingPanelUser}
        onSuccess={() => {
          refetch();
        }}
      />
      <SendPanelLinkModal
        visible={sendLinkModalVisible && !emailConflictModalVisible}
        onClose={() => setSendLinkModalVisible(false)}
        onSend={handleSendSurveyLink}
        isSending={isSendingSurveyLink}
      />
      <PanelUserEmailConflictModal
        visible={emailConflictModalVisible}
        onClose={() => {
          setEmailConflictModalVisible(false);
          setEmailConflicts([]);
          setEligibleConflictUsers([]);
          setCanRemoveConflictEmails(false);
          setPendingSendPayload(null);
        }}
        conflicts={emailConflicts}
        eligibleUsers={eligibleConflictUsers}
        showRemoveButton={canRemoveConflictEmails}
        onRemoveUsedAndSend={handleRemoveUsedEmailsAndSend}
        isSending={isSendingSurveyLink}
      />
      <ConfirmationModal
        visible={deleteConfirmVisible}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Are you sure you want to delete this panel user?"
        message="This action cannot be undone."
        cancelText="No"
        confirmText="Yes"
        confirmButtonStyle="danger"
      />
    </div>
  );
};

export default PanelUser;
